import React from 'react';
import PropTypes from 'prop-types';
import { Field } from 'redux-form';
import PoundIcon from 'mdi-react/PoundIcon';
import renderSelectField from '../../../../shared/components/form/Select';
import renderCheckBoxField from '../../../../shared/components/form/CheckBox';
import { renderFieldForm } from '../../shared/formComponents';
import {
  SETUP_FLOW_OPTIONS,
  AGENCY_INTERNAL_OPTIONS,
  INCLUDES_OPTIONS,
  COST_TARGET_TYPE_OPTIONS,
  SPEND_SPEED_OPTIONS,
  SPEND_TACTIC_OPTIONS,
  LIMITLESS_BUDGET_OPTIONS,
  SPEND_PRIORITY_OPTIONS,
} from '../../shared/constants';

/**
 * Part 1: Fields that appear BEFORE the Budget Type selector
 * This maintains the original field order from the legacy form
 */
const CommonBudgetFieldsPart1 = ({
  setupFlow,
  agencyInternal,
  costTargetType,
  formValues,
  activeDayOptions,
  user,
  retailerConfig,
}) => (
  <>
    {/* Budget Group Setup Flow */}
    <div className="form__form-group">
      <span className="form__form-group-label">Budget Group Setup Flow</span>
      <div className="form__form-group-field">
        <Field
          name="setupFlow"
          type="text"
          component={renderSelectField}
          options={SETUP_FLOW_OPTIONS}
        />
      </div>
    </div>

    {/* 3. View Toggle (admin only) */}
    {user.access === 'admin' && (
      <div className="form__form-group">
        <span className="form__form-group-label">View Toggle</span>
        <div className="form__form-group-field">
          <Field
            name="agencyInternal"
            type="text"
            component={renderSelectField}
            options={AGENCY_INTERNAL_OPTIONS}
            defaultValue={agencyInternal}
          />
        </div>
      </div>
    )}

    {/* 4. Allowed Days of the Week (advanced mode) */}
    {(setupFlow?.value || 'advanced') === 'advanced' && (
      <div className="form__form-group">
        <span className="form__form-group-label">Allowed Days of the Week</span>
        {activeDayOptions && activeDayOptions.map((day) => (
          <div className="form__form-group-field" key={day.name}>
            <Field
              name={day.name}
              component={renderCheckBoxField}
              label={day.label}
            />
          </div>
        ))}
      </div>
    )}

    {/* 5. Portfolio(s) (advanced + admin + amazon) */}
    {setupFlow?.value === 'advanced' && agencyInternal?.value && retailerConfig?.key === 'amazon' && (
      <div className="form__form-group">
        <span className="form__form-group-label typography-message">Portfolio(s)</span>
        <div className="form__form-group-field">
          <Field
            name="portfolioIdsCsv"
            type="text"
            component={renderFieldForm}
          />
        </div>
      </div>
    )}

    {/* 6. Includes (advanced + admin) */}
    {setupFlow?.value === 'advanced' && agencyInternal?.value && (
      <div className="form__form-group">
        <span className="form__form-group-label">Includes</span>
        <div className="form__form-group-field">
          <Field
            name="includes"
            type="text"
            component={renderSelectField}
            options={INCLUDES_OPTIONS}
          />
        </div>
      </div>
    )}

    {/* 7. Cost Target Type */}
    <div className="form__form-group">
      <span className="form__form-group-label">Cost Target Type</span>
      <div className="form__form-group-field">
        <Field
          name="costTargetType"
          type="text"
          component={renderSelectField}
          options={COST_TARGET_TYPE_OPTIONS}
        />
      </div>
    </div>

    {/* 8. Cost Target (ROAS) - blended variant */}
    {costTargetType?.value === 'blended' && (
      <div className="form__form-group">
        <span className="form__form-group-label typography-message">Cost Target (ROAS)</span>
        <div className="form__form-group-field">
          <div className="form__form-group-icon">
            <PoundIcon />
          </div>
          <Field
            name="costTarget"
            type="text"
            component={renderFieldForm}
          />
        </div>
        {formValues.costTarget && !Number.isNaN(parseFloat(formValues.costTarget)) && parseFloat(formValues.costTarget) > 0 && (
          <span className="form__form-group-label typography-message" style={{ fontSize: '0.9em', color: '#70bbfd', marginTop: '5px' }}>
            Estimated PACoS: {((1 / parseFloat(formValues.costTarget)) * 100).toFixed(2)}%
          </span>
        )}
      </div>
    )}

    {/* 8. Cost Target (ROAS) - brand/nonbrand variants */}
    {costTargetType?.value === 'bnb' && (
      <>
        <div className="form__form-group">
          <span className="form__form-group-label">Brand Cost Target (ROAS)</span>
          <div className="form__form-group-field">
            <div className="form__form-group-icon">
              <PoundIcon />
            </div>
            <Field
              name="costTargetBrand"
              type="text"
              component={renderFieldForm}
            />
          </div>
          {formValues.costTargetBrand && !Number.isNaN(parseFloat(formValues.costTargetBrand)) && parseFloat(formValues.costTargetBrand) > 0 && (
            <span className="form__form-group-label typography-message" style={{ fontSize: '0.9em', color: '#70bbfd', marginTop: '5px' }}>
              Estimated PACoS: {((1 / parseFloat(formValues.costTargetBrand)) * 100).toFixed(2)}%
            </span>
          )}
        </div>
        <div className="form__form-group">
          <span className="form__form-group-label">Non-Brand Cost Target (ROAS)</span>
          <div className="form__form-group-field">
            <div className="form__form-group-icon">
              <PoundIcon />
            </div>
            <Field
              name="costTargetNonBrand"
              type="text"
              component={renderFieldForm}
            />
          </div>
          {formValues.costTargetNonBrand && !Number.isNaN(parseFloat(formValues.costTargetNonBrand)) && parseFloat(formValues.costTargetNonBrand) > 0 && (
            <span className="form__form-group-label typography-message" style={{ fontSize: '0.9em', color: '#70bbfd', marginTop: '5px' }}>
              Estimated PACoS: {((1 / parseFloat(formValues.costTargetNonBrand)) * 100).toFixed(2)}%
            </span>
          )}
        </div>
      </>
    )}

    {/* 9. Spend Speed (advanced + admin) */}
    {setupFlow?.value === 'advanced' && agencyInternal?.value && (
      <div className="form__form-group">
        <span className="form__form-group-label">Spend Speed</span>
        <div className="form__form-group-field">
          <Field
            name="spendSpeed"
            type="text"
            component={renderSelectField}
            options={SPEND_SPEED_OPTIONS}
          />
        </div>
      </div>
    )}

    {/* 10. Spend Tactic (advanced + admin) */}
    {setupFlow?.value === 'advanced' && agencyInternal?.value && (
      <div className="form__form-group">
        <span className="form__form-group-label">Spend Tactic</span>
        <div className="form__form-group-field">
          <Field
            name="spendTactic"
            type="text"
            component={renderSelectField}
            options={SPEND_TACTIC_OPTIONS}
          />
        </div>
      </div>
    )}

    {/* 11. Limitless Budget (advanced + admin) */}
    {setupFlow?.value === 'advanced' && agencyInternal?.value && (
      <div className="form__form-group">
        <span className="form__form-group-label">Limitless Budget</span>
        <div className="form__form-group-field">
          <Field
            name="limitlessBudget"
            type="text"
            component={renderSelectField}
            options={LIMITLESS_BUDGET_OPTIONS}
          />
        </div>
      </div>
    )}

    {/* 12. Spend Priority */}
    <div className="form__form-group">
      <span className="form__form-group-label">Spend Priority</span>
      <div className="form__form-group-field">
        <Field
          name="spendPriority"
          type="text"
          component={renderSelectField}
          options={SPEND_PRIORITY_OPTIONS}
        />
      </div>
    </div>
  </>
);

CommonBudgetFieldsPart1.propTypes = {
  setupFlow: PropTypes.shape(),
  agencyInternal: PropTypes.shape(),
  costTargetType: PropTypes.shape(),
  formValues: PropTypes.shape(),
  activeDayOptions: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  user: PropTypes.shape().isRequired,
  retailerConfig: PropTypes.shape().isRequired,
};

CommonBudgetFieldsPart1.defaultProps = {
  setupFlow: null,
  agencyInternal: null,
  costTargetType: null,
  formValues: {},
};

export default CommonBudgetFieldsPart1;
